import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { ApiVerifyPasswordReq, ApiVerifyPasswordResp } from '@shared/authApiTypes';
import { useAuth } from '@/hooks/auth';
import { fetchWithTimeout } from '@/hooks/fetch';
import { processFetchError } from './errors';
import { EyeLogo, EyeLogoRef } from '@/components/EyeLogo';

export enum LogoutReasonHash {
  NONE = '',
  LOGOUT = '#logout',
  EXPIRED = '#expired',
  UPDATED = '#updated',
  MASTER_ALREADY_SET = '#master_already_set',
  SHUTDOWN = '#shutdown',
}

export default function Login() {
  const { setAuthData } = useAuth();
  const eyeRef = useRef<EyeLogoRef>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [capsOn, setCapsOn] = useState(false);

  // Idle blink every 6s
  useEffect(() => {
    const id = setInterval(() => {
      if (!isFetching) (eyeRef.current as any)?.blink?.() ?? eyeRef.current?.squint?.();
    }, 6000);
    return () => clearInterval(id);
  }, [isFetching]);

  // Handle logout reason hashes
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const messages: Record<string, string> = {
      [LogoutReasonHash.LOGOUT]:              'Logged out.',
      [LogoutReasonHash.EXPIRED]:             'Session expired.',
      [LogoutReasonHash.UPDATED]:             'SC Labs updated. Please login again.',
      [LogoutReasonHash.MASTER_ALREADY_SET]:  'Master account already configured.',
      [LogoutReasonHash.SHUTDOWN]:            'Server shut down. Restart FXServer to login.',
    };
    if (messages[hash]) setErrorMessage(messages[hash]);
    window.location.hash = '';
  }, []);

  // Autofill from localStorage (dev helper)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('authCredsAutofill');
      if (raw) {
        const [u, p] = JSON.parse(raw);
        if (u) setUsername(u);
        if (p) setPassword(p);
      }
    } catch (_) {}
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFetching) return;
    setIsFetching(true);
    setErrorMessage(undefined);

    try {
      const data = await fetchWithTimeout<ApiVerifyPasswordResp, ApiVerifyPasswordReq>(
        `/fivem/auth/login?uiVersion=${encodeURIComponent(window.txConsts.txaVersion)}`,
        { method: 'POST', body: { username, password } }
      );

      if ('error' in data) {
        if (data.error === 'refreshToUpdate') {
          window.location.href = `/login${LogoutReasonHash.UPDATED}`;
          window.location.reload();
        } else {
          setErrorMessage(data.error);
          eyeRef.current?.squint();
        }
        setIsFetching(false);
      } else {
        // Success — eye animation then auth
        eyeRef.current?.successSequence(() => {
          setAuthData(data);
        });
      }
    } catch (error: any) {
      const { errorTitle, errorMessage: em } = processFetchError(error);
      setErrorMessage(`${errorTitle}: ${em}`);
      eyeRef.current?.squint();
      setIsFetching(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
      style={{ background: 'var(--sc-bg)' }}
    >
      {/* Background: purple radial + grid texture */}
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(167,139,250,0.09) 0%, transparent 70%)' }}
      />

      {/* Glass card */}
      <div className="relative z-10 w-[360px] mx-4 flex flex-col items-center
                      glass-card sc-glow-purple px-8 py-8 gap-0"
        style={{ border: '1px solid rgba(167,139,250,0.15)' }}
      >
        {/* Top cyan accent line */}
        <div className="absolute top-0 left-8 right-8 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,209,0.4), rgba(167,139,250,0.4), transparent)' }}
        />
        {/* Eye */}
        <div className="pointer-events-none select-none mb-1">
          <EyeLogo ref={eyeRef} />
        </div>

        {/* Brand */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-black tracking-tight text-white leading-none">
            SC{' '}
            <span style={{ background: 'linear-gradient(90deg, #00ffd1, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Labs
            </span>
          </h1>

        </div>

        {/* Divider */}
        <div className="w-full h-px mb-6" style={{ background: 'var(--sc-border)' }} />

        {/* Form */}
        <form onSubmit={handleLogin} className="w-full flex flex-col gap-4">
          {/* Username */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-bold tracking-[0.15em] uppercase"
              style={{ color: 'var(--sc-muted)' }} htmlFor="sc-username">
              Username or Discord ID
            </label>
            <input
              id="sc-username"
              type="text"
              autoCapitalize="off"
              autoComplete="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isFetching}
              placeholder="username or discord id"
              className="w-full px-4 py-3 rounded-xl text-sm font-mono text-white placeholder:text-[#475569]
                         bg-white/[0.04] border border-white/[0.08] outline-none
                         focus:border-[rgba(0,255,209,0.4)] focus:ring-1 focus:ring-[rgba(0,255,209,0.15)]
                         disabled:opacity-50 transition-all duration-150"
            />
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-bold tracking-[0.15em] uppercase"
              style={{ color: 'var(--sc-muted)' }} htmlFor="sc-password">
              Password
            </label>
            <div className="relative">
              <input
                id="sc-password"
                type={showPass ? 'text' : 'password'}
                autoCapitalize="off"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={(e) => setCapsOn(e.getModifierState('CapsLock'))}
                onKeyDown={(e) => setCapsOn(e.getModifierState('CapsLock'))}
                disabled={isFetching}
                placeholder="password"
                className="w-full px-4 py-3 pr-10 rounded-xl text-sm font-mono text-white placeholder:text-[#475569]
                           bg-white/[0.04] border border-white/[0.08] outline-none
                           focus:border-[rgba(0,255,209,0.4)] focus:ring-1 focus:ring-[rgba(0,255,209,0.15)]
                           disabled:opacity-50 transition-all duration-150"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-white transition-colors"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {/* Caps-lock warning */}
            {capsOn && (
              <p className="flex items-center gap-1.5 mt-1.5 text-[11px] font-medium" style={{ color: 'var(--sc-warn)' }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--sc-warn)' }} /> Caps Lock is on
              </p>
            )}
          </div>

          {/* Error */}
          {errorMessage && (
            <p className="text-xs text-center font-medium text-red-400 whitespace-pre-wrap" role="alert">
              {errorMessage}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isFetching || !username || !password}
            className="w-full py-3 rounded-xl text-sm font-bold tracking-wide text-white mt-1
                       disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150
                       hover:brightness-110 active:scale-[0.98]"
            style={{ background: 'linear-gradient(90deg, rgba(0,255,209,0.75), rgba(167,139,250,0.75))' }}
          >
            {isFetching ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Authenticating...
              </span>
            ) : (
              'Sign In →'
            )}
          </button>
        </form>

        {/* Footer status */}
        <div className="flex items-center gap-2 mt-6 text-[9px] font-bold tracking-[0.15em] uppercase"
          style={{ color: 'var(--sc-muted)' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: 'var(--sc-green)', boxShadow: '0 0 6px var(--sc-green)' }} />
          System Online
        </div>
      </div>
    </div>
  );
}
